import classNames from 'classnames';
import { Button, Popover } from 'antd';
import type { FC } from 'react';

import { InfoCircle, Logo, Redo } from '@shared/assets/icons/components';
import { Text } from '@shared/ui/antd';

import type { AgentTestFieldInfo } from '../lib/types';
import styles from '../agent-test-chat.module.scss';

const POPOVER_TEXT_LIMIT = 35;

interface AgentTestHeaderProps {
  title: string;
  onClear: () => void;
  isClearing?: boolean;
  isClearDisabled?: boolean;
  clearLabel?: string;
  leadFields: AgentTestFieldInfo[];
  clientFields: AgentTestFieldInfo[];
  isLeadCreated: boolean;
  leadTitle: string;
  clientTitle: string;
  leadCreatedLabel: string;
  leadNotCreatedLabel: string;
  notFilledLabel: string;
  infoLabel: string;
}

export const AgentTestHeader: FC<AgentTestHeaderProps> = ({
  title,
  onClear,
  isClearing = false,
  isClearDisabled = false,
  clearLabel,
  leadFields,
  clientFields,
  isLeadCreated,
  leadTitle,
  clientTitle,
  leadCreatedLabel,
  leadNotCreatedLabel,
  notFilledLabel,
  infoLabel,
}) => {
  const renderFields = (fields: AgentTestFieldInfo[]) => {
    return fields.map((field) => {
      const isFilled = Boolean(field.value.trim());
      const fieldValue = isFilled ? field.value : notFilledLabel;

      return (
        <div key={field.id} className={styles.infoField}>
          <Text
            variant="xs"
            className={styles.infoFieldLabel}
            hasTooltip
            maxLength={POPOVER_TEXT_LIMIT}
          >
            {field.label}
          </Text>
          <div
            className={classNames(styles.infoFieldValue, {
              [styles.infoFieldValueEmpty]: !isFilled,
            })}
          >
            <Text
              variant="sm"
              hasTooltip
              maxLength={POPOVER_TEXT_LIMIT}
              style={!isFilled ? { color: 'var(--color-text-disabled)' } : undefined}
            >
              {fieldValue}
            </Text>
          </div>
        </div>
      );
    });
  };

  const popoverContent = (
    <div className={styles.infoPopover}>
      <div className={styles.infoSection}>
        <div className={styles.infoSectionHeader}>
          <Text variant="sm" fontWeight={500}>
            {leadTitle}
          </Text>

          <span
            className={classNames(styles.infoStatusTag, {
              [styles.infoStatusTagCreated]: isLeadCreated,
            })}
          >
            {isLeadCreated ? leadCreatedLabel : leadNotCreatedLabel}
          </span>
        </div>
        {renderFields(leadFields)}
      </div>

      {clientFields.length > 0 && (
        <div className={styles.infoSection}>
          <Text variant="lg" fontWeight={500}>
            {clientTitle}
          </Text>
          {renderFields(clientFields)}
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.header}>
      <div className={styles.headerContent}>
        <div className={styles.headerIcon}>
          <Logo width={32} height={32} />
        </div>
        <div className={styles.headerText}>
          <Text variant="md" fontWeight={500}>
            {title}
          </Text>
        </div>
      </div>

      <div className={styles.headerActions}>
        <Button
          type="text"
          icon={<Redo />}
          className={styles.headerAction}
          onClick={onClear}
          loading={isClearing}
          disabled={isClearing || isClearDisabled}
          aria-label={clearLabel}
        />
        <Popover trigger="hover" placement="bottomRight" content={popoverContent} arrow={false}>
          <button type="button" className={styles.headerAction} aria-label={infoLabel}>
            <InfoCircle />
          </button>
        </Popover>
      </div>
    </div>
  );
};
